import { useQuery } from '@tanstack/react-query';
import {
  type User,
  V2AdminUsersService,
  type ApiQueryParams,
} from 'src/api/generated';
import type { ApiResponse } from 'src/hooks/api/api';

interface UsersResponse extends ApiResponse {
  users: User[];
}

export const adminUsersQueryKeys = {
  list: (queryParams: ApiQueryParams = {}) =>
    ['admin/users', queryParams] as const,
  detail: (id: number) => ['admin/users', id] as const,
  current: () => ['admin/users/current'] as const,
  all: (filters: { filterBy?: string; searchBy?: string; sortBy?: string }) =>
    ['admin/users/all', filters] as const,
};

export const getCurrentUserQueryOptions = () => ({
  queryKey: adminUsersQueryKeys.current(),
  queryFn: async (): Promise<User> => {
    return await V2AdminUsersService.getCurrentUser();
  },
});

/**
 * Hook to fetch users with pagination support.
 */
export const useUsers = (queryParams: ApiQueryParams = {}): UsersResponse => {
  const fetchUsers = async (): Promise<User[]> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = queryParams;

    return await V2AdminUsersService.getUsers(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize
    );
  };

  const {
    data: users,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: adminUsersQueryKeys.list(queryParams),
    queryFn: fetchUsers,
  });

  return {
    loading: isLoading,
    error,
    users: users ?? [],
    refetch,
  };
};

interface UseUserResponse extends ApiResponse {
  user: User | null;
}

export const useUser = (id: number, enabled: boolean): UseUserResponse => {
  const fetchUser = async (): Promise<User> => {
    return await V2AdminUsersService.getUser(id);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: adminUsersQueryKeys.detail(id),
    queryFn: fetchUser,
    enabled,
  });

  return {
    loading,
    error,
    user: data ?? null,
    refetch,
  };
};

export const useCurrentUser = (enabled = true): UseUserResponse => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    ...getCurrentUserQueryOptions(),
    enabled,
  });

  return {
    loading,
    error,
    user: data ?? null,
    refetch,
  };
};

/**
 * Hook to fetch ALL users by iterating through all pages.
 * Use this when you truly need all users (e.g., dropdowns in tenants with >500 users).
 * For most cases, use the useUsers hook with pagination parameters.
 *
 * @param filters - Optional filters to apply (searchBy, filterBy, sortBy)
 * @param enabled - Whether the paginated request is allowed to run.
 * @returns All users matching the filters
 */
export const useAllUsers = (
  filters: {
    filterBy?: string;
    searchBy?: string;
    sortBy?: string;
  } = {},
  enabled = true
): UsersResponse => {
  const fetchAllUsers = async (): Promise<User[]> => {
    const { filterBy, searchBy, sortBy } = filters;
    const PAGE_SIZE = 100;
    const MAX_PAGES = 10;

    let page = 1;
    let allUsers: User[] = [];
    let hasMore = true;

    while (hasMore) {
      const users = await V2AdminUsersService.getUsers(
        filterBy,
        searchBy,
        sortBy,
        page,
        PAGE_SIZE
      );

      allUsers = [...allUsers, ...users];

      // If we got fewer users than pageSize, we've reached the last page
      hasMore = users.length === PAGE_SIZE;
      page++;

      // Safety check to prevent infinite loops (max 10 pages = 1,000 users)
      if (page > MAX_PAGES) {
        console.warn(
          `useAllUsers: Reached maximum page limit (${MAX_PAGES} pages, ${MAX_PAGES * PAGE_SIZE} users). There may be more users.`
        );
        break;
      }
    }

    return allUsers;
  };

  const {
    data: users,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: adminUsersQueryKeys.all(filters),
    queryFn: fetchAllUsers,
    enabled,
    // Cache for 5 minutes since fetching all users is expensive
    staleTime: 5 * 60 * 1000,
  });

  return {
    loading: enabled && isLoading,
    error: enabled ? error : null,
    users: enabled ? users ?? [] : [],
    refetch: enabled ? refetch : () => Promise.resolve(undefined),
  };
};
